import { DEFAULT_TOPOLOGY, normalizeType, rdflib, defaultNS, RENDER_CLASS_NAME, getTermBestLang, getPropBestLangRaw, LinkedRenderStore } from 'link-lib';
import React__default, { createContext, useContext, createElement, useState, useEffect, forwardRef, Fragment, memo, Component, useMemo } from 'react';
import { ACCEPTED, BAD_REQUEST } from 'http-status-codes';
import { shape, oneOf, string as string$1, oneOfType, arrayOf, instanceOf, node, object as object$1 } from 'prop-types';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __spreadArrays() {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
}

(function (Object) {
  typeof globalThis !== 'object' && (
    this ?
      get() :
      (Object.defineProperty(Object.prototype, '_T_', {
        configurable: true,
        get: get
      }), _T_)
  );
  function get() {
    this.globalThis = this;
    delete Object.prototype._T_;
  }
}(Object));

var TermType;
(function (TermType) {
    TermType["NamedNode"] = "NamedNode";
    TermType["BlankNode"] = "BlankNode";
    TermType["Literal"] = "Literal";
})(TermType || (TermType = {}));
var QuadPosition;
(function (QuadPosition) {
    QuadPosition[QuadPosition["subject"] = 0] = "subject";
    QuadPosition[QuadPosition["predicate"] = 1] = "predicate";
    QuadPosition[QuadPosition["object"] = 2] = "object";
    QuadPosition[QuadPosition["graph"] = 3] = "graph";
})(QuadPosition || (QuadPosition = {}));
var Feature;
(function (Feature) {
    /** Whether the factory supports termType:Collection terms */
    Feature["collections"] = "COLLECTIONS";
    /** Whether the factory supports termType:DefaultGraph terms */
    Feature["defaultGraphType"] = "DEFAULT_GRAPH_TYPE";
    /** Whether the factory supports equals on produced instances */
    Feature["equalsMethod"] = "EQUALS_METHOD";
    /** Whether the factory supports mapping ids back to instances */
    Feature["nodeLookup"] = "NODE_LOOKUP";
    /** Whether the factory supports termType:Variable terms */
    Feature["variableType"] = "VARIABLE_TYPE";
})(Feature || (Feature = {}));

var datatypes = {
    boolean: "http://www.w3.org/2001/XMLSchema#boolean",
    dateTime: "http://www.w3.org/2001/XMLSchema#dateTime",
    double: "http://www.w3.org/2001/XMLSchema#double",
    decimal: "http://www.w3.org/2001/XMLSchema#decimal",
    integer: "http://www.w3.org/2001/XMLSchema#integer",
    langString: "http://www.w3.org/1999/02/22-rdf-syntax-ns#langString",
    string: "http://www.w3.org/2001/XMLSchema#string"
};
function equals(a, b) {
    if (a === b) {
        return true;
    }
    if (!a || !b) {
        return a === b;
    }
    if (Object.prototype.hasOwnProperty.call(a, 'equals')) {
        return a.equals(b);
    }
    if (Array.isArray(a) || Array.isArray(b)) {
        if (!(Array.isArray(a) && Array.isArray(b))) {
            return false;
        }
        return equals(a[0], b[0])
            && equals(a[1], b[1])
            && equals(a[2], b[2])
            && equals(a[3], b[3]);
    }
    if (PlainFactory.isQuad(a) || PlainFactory.isQuad(b)) {
        if (!PlainFactory.isQuad(a) || !PlainFactory.isQuad(b)) {
            return false;
        }
        return equals(a.subject, b.subject)
            && equals(a.predicate, b.predicate)
            && equals(a.object, b.object)
            && equals(a.graph, b.graph);
    }
    switch (a.termType) {
        case 'Literal': {
            return a.termType === b.termType
                && a.value === b.value
                && b.datatype === b.datatype
                && b.language === b.language;
        }
        default:
            if (!a.termType || !b.termType) {
                return false;
            }
            return a.termType === b.termType
                && a.value === b.value;
    }
}
var RDFBase = {
    equals: function (other) {
        return equals(this, other);
    },
    /* rdflib compat */
    toCanonical: function () {
        return this;
    }
};
/**
 * Plain JS/functional implementation of the RDF/JS: Data model specification, limited to a strict
 * rdf subset (no Variable, no Literal as predicate, etc.).
 */
var PlainFactory = /** @class */ (function () {
    function PlainFactory(opts) {
        var _a;
        if (opts === void 0) { opts = {}; }
        this.bnIndex = opts.bnIndex || 0;
        this.supports = opts.supports || (_a = {},
            _a[Feature.collections] = false,
            _a[Feature.defaultGraphType] = false,
            _a[Feature.equalsMethod] = false,
            _a[Feature.nodeLookup] = false,
            _a[Feature.variableType] = false,
            _a);
    }
    /**
     * Checks if the object {obj} is a Quad.
     */
    PlainFactory.isQuad = function (obj) {
        if (Array.isArray(obj) || !obj) {
            return false;
        }
        return !!(Object.prototype.hasOwnProperty.call(obj, "subject")
            && Object.prototype.hasOwnProperty.call(obj, "predicate")
            && Object.prototype.hasOwnProperty.call(obj, "object"));
    };
    PlainFactory.prototype.namedNode = function (value) {
        var term = Object.create(RDFBase);
        term.termType = TermType.NamedNode;
        term.value = value;
        return term;
    };
    PlainFactory.prototype.blankNode = function (value) {
        var term = Object.create(RDFBase);
        term.termType = TermType.BlankNode;
        term.value = value || "b" + ++this.bnIndex;
        return term;
    };
    PlainFactory.prototype.literal = function (value, languageOrDatatype) {
        if (typeof value !== "string") {
            return this.parseLiteral(value);
        }
        var term = Object.create(RDFBase);
        term.termType = TermType.Literal;
        // Perf: Always set all properties to prevent the creation of unnecessary HiddenClass objects.
        term.language = typeof languageOrDatatype === "string" ? languageOrDatatype : undefined;
        term.datatype = typeof languageOrDatatype === "string"
            ? this.namedNode(datatypes.langString)
            : languageOrDatatype || this.namedNode(datatypes.string);
        term.value = value;
        return term;
    };
    PlainFactory.prototype.defaultGraph = function () {
        return this.namedNode("");
    };
    /**
     * Create an RDF statement in object form.
     * @param subject The subject of the statement
     * @param predicate The predicate of the statement
     * @param object The object of the statement
     * @param graph The graph of the statement
     */
    PlainFactory.prototype.quad = function (subject, predicate, object, graph) {
        return {
            subject: subject,
            predicate: predicate,
            object: object,
            graph: graph || this.defaultGraph()
        };
    };
    PlainFactory.prototype.isQuad = function (obj) {
        return PlainFactory.isQuad(obj);
    };
    /**
     * * Returns an RDF statement in array form.
     * @param subject The subject of the statement
     * @param predicate The predicate of the statement
     * @param object The object of the statement
     * @param graph The graph of the statement
     */
    PlainFactory.prototype.quadruple = function (subject, predicate, object, graph) {
        return [
            subject,
            predicate,
            object,
            graph || this.defaultGraph(),
        ];
    };
    PlainFactory.prototype.fromTerm = function (original) {
        if (original.termType === "Literal") {
            return this.literal(original.value, original.language || original.datatype);
        }
        return {
            termType: original.termType,
            value: original.value
        };
    };
    PlainFactory.prototype.fromQuad = function (original) {
        return {
            subject: original.subject,
            predicate: original.predicate,
            object: original.object,
            graph: original.graph || this.defaultGraph()
        };
    };
    PlainFactory.prototype.fromQdr = function (original) {
        return {
            subject: original[0],
            predicate: original[1],
            object: original[2],
            graph: original[3] || this.defaultGraph()
        };
    };
    PlainFactory.prototype.qdrFromQuad = function (original) {
        return [
            original.subject,
            original.predicate,
            original.object,
            original.graph || this.defaultGraph(),
        ];
    };
    PlainFactory.prototype.qdrFromQdr = function (original) {
        return [
            original[0],
            original[1],
            original[2],
            original[3] || this.defaultGraph(),
        ];
    };
    /**
     * Compare if two RDF objects are the same.
     *
     * Should work with non-standard rdf libraries as well (e.g. supporting Variable and Collection).
     */
    PlainFactory.prototype.equals = function (a, b) {
        return equals(a, b);
    };
    PlainFactory.prototype.id = function (obj) {
        return this.toNQ(obj);
    };
    PlainFactory.prototype.toNQ = function (term) {
        if (Array.isArray(term)) {
            var graph = term[3] === this.defaultGraph() ? '' : (this.toNQ(term[3]) + ' ');
            return this.toNQ(term[0]) + " " + this.toNQ(term[1]) + " " + this.toNQ(term[2]) + " " + graph + ".";
        }
        else if (this.isQuad(term)) {
            return this.quadToNQ(term);
        }
        return this.termToNQ(term);
    };
    PlainFactory.prototype.parseLiteral = function (value) {
        var literalValue = undefined;
        var datatype = undefined;
        if (typeof value === "number" || value instanceof Number) {
            // From rdflib; https://github.com/linkeddata/rdflib.js/blob/master/src/literal.js#L104
            if (value.toString().indexOf('e') < 0 && Math.ceil(value) <= Number.MAX_SAFE_INTEGER) {
                datatype = Number.isInteger(value) ? datatypes.integer : datatypes.decimal;
            }
            else {
                datatype = datatypes.double;
            }
            literalValue = value.toString();
        }
        else if (typeof value === "boolean" || value instanceof Boolean) {
            literalValue = value.toString();
            datatype = datatypes.boolean;
        }
        else if (typeof value === "bigint") {
            datatype = datatypes.integer;
            literalValue = value.toString();
        }
        else if (typeof value === "object") {
            if (value instanceof Date) {
                if (isNaN(value.getTime())) {
                    throw new Error('Invalid date given');
                }
                literalValue = value.toISOString();
                datatype = datatypes.dateTime;
            }
        }
        if (typeof datatype !== "undefined") {
            return this.literal(literalValue, this.namedNode(datatype));
        }
        throw new Error("Can't parse value '" + value + "'");
    };
    PlainFactory.prototype.termToNQ = function (term) {
        switch (term.termType) {
            case TermType.BlankNode:
                return "_:" + term.value;
            case TermType.NamedNode:
                return "<" + term.value + ">";
            case TermType.Literal:
                if (term.datatype) {
                    return term.value + "^^" + this.termToNQ(term.datatype);
                }
                return term.value + "@" + term.language;
            default:
                throw new Error("Nonstandard termtype '" + term.termType + "' given");
        }
    };
    PlainFactory.prototype.quadrupleToNQ = function (quad) {
        var graph = !quad[QuadPosition.graph] || quad[QuadPosition.graph] === this.defaultGraph()
            ? ''
            : (this.toNQ(quad[QuadPosition.graph]) + ' ');
        return this.toNQ(quad[QuadPosition.subject]) + " " + this.toNQ(quad[QuadPosition.predicate]) + " " + this.toNQ(quad[QuadPosition.object]) + " " + graph + ".\n";
    };
    PlainFactory.prototype.quadToNQ = function (quad) {
        return this.quadrupleToNQ([
            quad.subject,
            quad.predicate,
            quad.object,
            quad.graph || this.defaultGraph(),
        ]);
    };
    return PlainFactory;
}());
/**
 * Plain JS/functional implementation of the RDF/JS: Data model specification, limited to a strict
 * rdf subset (no Variable, no Literal as predicate, etc.).
 */
var DefaultFactory = new PlainFactory();

function isTerm(obj) {
    return obj && Object.prototype.hasOwnProperty.call(obj, "termType");
}
function isNamedNode(obj) {
    return isTerm(obj) && obj.termType === TermType.NamedNode;
}
function isLiteral(obj) {
    return isTerm(obj) && obj.termType === TermType.Literal;
}
var TermTypes = [
    TermType.NamedNode.toString(),
    TermType.BlankNode.toString(),
    TermType.Literal.toString()
];
var NodeTypes = [TermType.NamedNode.toString(), TermType.BlankNode.toString()];

var globalFactory;
if (typeof Symbol !== "undefined") {
    var rdfFactory_1 = Symbol["for"]('rdfFactory');
    globalFactory = globalThis[rdfFactory_1];
}
else {
    var rdfFactory_2 = 'rdfFactory';
    globalFactory = globalThis[rdfFactory_2];
}
var createNS = function (ns) {
    return function (term) {
        return globalFactory.namedNode("" + ns + term);
    };
};
var proxy;
if (typeof Proxy !== "undefined") {
    proxy = new Proxy(globalFactory || {}, {
        ownKeys: function () {
            return globalFactory && Object.keys(globalFactory) || [];
        },
        getOwnPropertyDescriptor: function (_, k) {
            return Object.getOwnPropertyDescriptor(globalFactory, k);
        },
        set: function (_, property, value) {
            globalFactory[property] = value;
            return true;
        },
        get: function (_, property) {
            return globalFactory[property];
        }
    });
}
else {
    proxy = [
        'namedNode',
        'blankNode',
        'literal',
        'defaultGraph',
        'quad',
        'quadruple',
        'fromTerm',
        'fromQuad',
        'fromQdr',
        'qdrFromQuad',
        'qdrFromQdr',
        'equals',
        'id',
        'toNQ',
        'findById',
        'termToNQ',
        'quadrupleToNQ',
        'quadToNQ',
    ].reduce(function (acc, key) {
        acc[key] = function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return globalFactory[key].apply(globalFactory, args);
        };
        return acc;
    }, {});
}
var rdfFactory = proxy;

/** Function to create arbitrary terms within the 'Resource Description Framework' ontology */
const ns = createNS("http://www.w3.org/1999/02/22-rdf-syntax-ns#");
/** The datatype of RDF literals storing fragments of HTML content */
const HTML = ns("HTML");
/** The datatype of language-tagged string values */
const langString = ns("langString");
/** The class of plain (i.e. untyped) literal values, as used in RIF and OWL 2 */
const PlainLiteral = ns("PlainLiteral");
/** The class of RDF properties. */
const Property = ns("Property");
/** The class of RDF statements. */
const Statement = ns("Statement");
/** The class of unordered containers. */
const Bag = ns("Bag");
/** The class of ordered containers. */
const Seq = ns("Seq");
/** The class of containers of alternatives. */
const Alt = ns("Alt");
/** The class of RDF Lists. */
const List = ns("List");
/** The empty list, with no items in it. If the rest of a list is nil then the list has no more items in it. */
const nil = ns("nil");
/** The datatype of XML literal values. */
const XMLLiteral = ns("XMLLiteral");
/** The subject is an instance of a class. */
const type = ns("type");
/** The subject of the subject RDF statement. */
const subject = ns("subject");
/** The predicate of the subject RDF statement. */
const predicate = ns("predicate");
/** The object of the subject RDF statement. */
const object = ns("object");
/** Idiomatic property used for structured values. */
const value = ns("value");
/** The first item in the subject RDF list. */
const first = ns("first");
/** The rest of the subject RDF list after the first item. */
const rest = ns("rest");
var rdf = {
    /** Function to create arbitrary terms within the 'Resource Description Framework' ontology */
    ns,
    /** The datatype of RDF literals storing fragments of HTML content */
    HTML,
    /** The datatype of language-tagged string values */
    langString,
    /** The class of plain (i.e. untyped) literal values, as used in RIF and OWL 2 */
    PlainLiteral,
    /** The class of RDF properties. */
    Property,
    /** The class of RDF statements. */
    Statement,
    /** The class of unordered containers. */
    Bag,
    /** The class of ordered containers. */
    Seq,
    /** The class of containers of alternatives. */
    Alt,
    /** The class of RDF Lists. */
    List,
    /** The empty list, with no items in it. If the rest of a list is nil then the list has no more items in it. */
    nil,
    /** The datatype of XML literal values. */
    XMLLiteral,
    /** The subject is an instance of a class. */
    type,
    /** The subject of the subject RDF statement. */
    subject,
    /** The predicate of the subject RDF statement. */
    predicate,
    /** The object of the subject RDF statement. */
    object,
    /** Idiomatic property used for structured values. */
    value,
    /** The first item in the subject RDF list. */
    first,
    /** The rest of the subject RDF list after the first item. */
    rest
};

/** Function to create arbitrary terms within the 'RDF-Schema' ontology */
const ns$1 = createNS("http://www.w3.org/2000/01/rdf-schema#");
/** The class resource, everything. */
const Resource = ns$1("Resource");
/** The class of classes. */
const Class = ns$1("Class");
/** The class of literal values, eg. textual strings and integers. */
const Literal = ns$1("Literal");
/** The class of RDF containers. */
const Container = ns$1("Container");
/** The class of container membership properties, rdf:_1, rdf:_2, ...,
                    all of which are sub-properties of 'member'. */
const ContainerMembershipProperty = ns$1("ContainerMembershipProperty");
/** The class of RDF datatypes. */
const Datatype = ns$1("Datatype");
/** The subject is a subclass of a class. */
const subClassOf = ns$1("subClassOf");
/** The subject is a subproperty of a property. */
const subPropertyOf = ns$1("subPropertyOf");
/** A description of the subject resource. */
const comment = ns$1("comment");
/** A human-readable name for the subject. */
const label = ns$1("label");
/** A domain of the subject property. */
const domain = ns$1("domain");
/** A range of the subject property. */
const range = ns$1("range");
/** Further information about the subject resource. */
const seeAlso = ns$1("seeAlso");
/** The defininition of the subject resource. */
const isDefinedBy = ns$1("isDefinedBy");
/** A member of the subject resource. */
const member = ns$1("member");
var rdfs = {
    /** Function to create arbitrary terms within the 'RDF-Schema' ontology */
    ns: ns$1,
    /** The class resource, everything. */
    Resource,
    /** The class of classes. */
    Class,
    /** The class of literal values, eg. textual strings and integers. */
    Literal,
    /** The class of RDF containers. */
    Container,
    /** The class of container membership properties, rdf:_1, rdf:_2, ...,
                        all of which are sub-properties of 'member'. */
    ContainerMembershipProperty,
    /** The class of RDF datatypes. */
    Datatype,
    /** The subject is a subclass of a class. */
    subClassOf,
    /** The subject is a subproperty of a property. */
    subPropertyOf,
    /** A description of the subject resource. */
    comment,
    /** A human-readable name for the subject. */
    label,
    /** A domain of the subject property. */
    domain,
    /** A range of the subject property. */
    range,
    /** Further information about the subject resource. */
    seeAlso,
    /** The defininition of the subject resource. */
    isDefinedBy,
    /** A member of the subject resource. */
    member
};

function unwrapExports (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var reactIs_production_min = createCommonjsModule(function (module, exports) {
Object.defineProperty(exports,"__esModule",{value:!0});
var b="function"===typeof Symbol&&Symbol.for,c=b?Symbol.for("react.element"):60103,d=b?Symbol.for("react.portal"):60106,e=b?Symbol.for("react.fragment"):60107,f=b?Symbol.for("react.strict_mode"):60108,g=b?Symbol.for("react.profiler"):60114,h=b?Symbol.for("react.provider"):60109,k=b?Symbol.for("react.context"):60110,l=b?Symbol.for("react.async_mode"):60111,m=b?Symbol.for("react.concurrent_mode"):60111,n=b?Symbol.for("react.forward_ref"):60112,p=b?Symbol.for("react.suspense"):60113,q=b?Symbol.for("react.suspense_list"):
60120,r=b?Symbol.for("react.memo"):60115,t=b?Symbol.for("react.lazy"):60116,v=b?Symbol.for("react.fundamental"):60117,w=b?Symbol.for("react.responder"):60118;function x(a){if("object"===typeof a&&null!==a){var u=a.$$typeof;switch(u){case c:switch(a=a.type,a){case l:case m:case e:case g:case f:case p:return a;default:switch(a=a&&a.$$typeof,a){case k:case n:case h:return a;default:return u}}case t:case r:case d:return u}}}function y(a){return x(a)===m}exports.typeOf=x;exports.AsyncMode=l;
exports.ConcurrentMode=m;exports.ContextConsumer=k;exports.ContextProvider=h;exports.Element=c;exports.ForwardRef=n;exports.Fragment=e;exports.Lazy=t;exports.Memo=r;exports.Portal=d;exports.Profiler=g;exports.StrictMode=f;exports.Suspense=p;
exports.isValidElementType=function(a){return "string"===typeof a||"function"===typeof a||a===e||a===m||a===g||a===f||a===p||a===q||"object"===typeof a&&null!==a&&(a.$$typeof===t||a.$$typeof===r||a.$$typeof===h||a.$$typeof===k||a.$$typeof===n||a.$$typeof===v||a.$$typeof===w)};exports.isAsyncMode=function(a){return y(a)||x(a)===l};exports.isConcurrentMode=y;exports.isContextConsumer=function(a){return x(a)===k};exports.isContextProvider=function(a){return x(a)===h};
exports.isElement=function(a){return "object"===typeof a&&null!==a&&a.$$typeof===c};exports.isForwardRef=function(a){return x(a)===n};exports.isFragment=function(a){return x(a)===e};exports.isLazy=function(a){return x(a)===t};exports.isMemo=function(a){return x(a)===r};exports.isPortal=function(a){return x(a)===d};exports.isProfiler=function(a){return x(a)===g};exports.isStrictMode=function(a){return x(a)===f};exports.isSuspense=function(a){return x(a)===p};
});

unwrapExports(reactIs_production_min);
var reactIs_production_min_1 = reactIs_production_min.typeOf;
var reactIs_production_min_2 = reactIs_production_min.AsyncMode;
var reactIs_production_min_3 = reactIs_production_min.ConcurrentMode;
var reactIs_production_min_4 = reactIs_production_min.ContextConsumer;
var reactIs_production_min_5 = reactIs_production_min.ContextProvider;
var reactIs_production_min_6 = reactIs_production_min.Element;
var reactIs_production_min_7 = reactIs_production_min.ForwardRef;
var reactIs_production_min_8 = reactIs_production_min.Fragment;
var reactIs_production_min_9 = reactIs_production_min.Lazy;
var reactIs_production_min_10 = reactIs_production_min.Memo;
var reactIs_production_min_11 = reactIs_production_min.Portal;
var reactIs_production_min_12 = reactIs_production_min.Profiler;
var reactIs_production_min_13 = reactIs_production_min.StrictMode;
var reactIs_production_min_14 = reactIs_production_min.Suspense;
var reactIs_production_min_15 = reactIs_production_min.isValidElementType;
var reactIs_production_min_16 = reactIs_production_min.isAsyncMode;
var reactIs_production_min_17 = reactIs_production_min.isConcurrentMode;
var reactIs_production_min_18 = reactIs_production_min.isContextConsumer;
var reactIs_production_min_19 = reactIs_production_min.isContextProvider;
var reactIs_production_min_20 = reactIs_production_min.isElement;
var reactIs_production_min_21 = reactIs_production_min.isForwardRef;
var reactIs_production_min_22 = reactIs_production_min.isFragment;
var reactIs_production_min_23 = reactIs_production_min.isLazy;
var reactIs_production_min_24 = reactIs_production_min.isMemo;
var reactIs_production_min_25 = reactIs_production_min.isPortal;
var reactIs_production_min_26 = reactIs_production_min.isProfiler;
var reactIs_production_min_27 = reactIs_production_min.isStrictMode;
var reactIs_production_min_28 = reactIs_production_min.isSuspense;

var reactIs_development = createCommonjsModule(function (module, exports) {



if (process.env.NODE_ENV !== "production") {
  (function() {

Object.defineProperty(exports, '__esModule', { value: true });

// The Symbol used to tag the ReactElement-like types. If there is no native Symbol
// nor polyfill, then a plain number is used for performance.
var hasSymbol = typeof Symbol === 'function' && Symbol.for;

var REACT_ELEMENT_TYPE = hasSymbol ? Symbol.for('react.element') : 0xeac7;
var REACT_PORTAL_TYPE = hasSymbol ? Symbol.for('react.portal') : 0xeaca;
var REACT_FRAGMENT_TYPE = hasSymbol ? Symbol.for('react.fragment') : 0xeacb;
var REACT_STRICT_MODE_TYPE = hasSymbol ? Symbol.for('react.strict_mode') : 0xeacc;
var REACT_PROFILER_TYPE = hasSymbol ? Symbol.for('react.profiler') : 0xead2;
var REACT_PROVIDER_TYPE = hasSymbol ? Symbol.for('react.provider') : 0xeacd;
var REACT_CONTEXT_TYPE = hasSymbol ? Symbol.for('react.context') : 0xeace;
// TODO: We don't use AsyncMode or ConcurrentMode anymore. They were temporary
// (unstable) APIs that have been removed. Can we remove the symbols?
var REACT_ASYNC_MODE_TYPE = hasSymbol ? Symbol.for('react.async_mode') : 0xeacf;
var REACT_CONCURRENT_MODE_TYPE = hasSymbol ? Symbol.for('react.concurrent_mode') : 0xeacf;
var REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for('react.forward_ref') : 0xead0;
var REACT_SUSPENSE_TYPE = hasSymbol ? Symbol.for('react.suspense') : 0xead1;
var REACT_SUSPENSE_LIST_TYPE = hasSymbol ? Symbol.for('react.suspense_list') : 0xead8;
var REACT_MEMO_TYPE = hasSymbol ? Symbol.for('react.memo') : 0xead3;
var REACT_LAZY_TYPE = hasSymbol ? Symbol.for('react.lazy') : 0xead4;
var REACT_FUNDAMENTAL_TYPE = hasSymbol ? Symbol.for('react.fundamental') : 0xead5;
var REACT_RESPONDER_TYPE = hasSymbol ? Symbol.for('react.responder') : 0xead6;

function isValidElementType(type) {
  return typeof type === 'string' || typeof type === 'function' ||
  // Note: its typeof might be other than 'symbol' or 'number' if it's a polyfill.
  type === REACT_FRAGMENT_TYPE || type === REACT_CONCURRENT_MODE_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || typeof type === 'object' && type !== null && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_RESPONDER_TYPE);
}

/**
 * Forked from fbjs/warning:
 * https://github.com/facebook/fbjs/blob/e66ba20ad5be433eb54423f2b097d829324d9de6/packages/fbjs/src/__forks__/warning.js
 *
 * Only change is we use console.warn instead of console.error,
 * and do nothing when 'console' is not supported.
 * This really simplifies the code.
 * ---
 * Similar to invariant but only logs a warning if the condition is not met.
 * This can be used to log issues in development environments in critical
 * paths. Removing the logging code for production environments will keep the
 * same logic and follow the same code paths.
 */

var lowPriorityWarning = function () {};

{
  var printWarning = function (format) {
    for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    var argIndex = 0;
    var message = 'Warning: ' + format.replace(/%s/g, function () {
      return args[argIndex++];
    });
    if (typeof console !== 'undefined') {
      console.warn(message);
    }
    try {
      // --- Welcome to debugging React ---
      // This error was thrown as a convenience so that you can use this stack
      // to find the callsite that caused this warning to fire.
      throw new Error(message);
    } catch (x) {}
  };

  lowPriorityWarning = function (condition, format) {
    if (format === undefined) {
      throw new Error('`lowPriorityWarning(condition, format, ...args)` requires a warning ' + 'message argument');
    }
    if (!condition) {
      for (var _len2 = arguments.length, args = Array(_len2 > 2 ? _len2 - 2 : 0), _key2 = 2; _key2 < _len2; _key2++) {
        args[_key2 - 2] = arguments[_key2];
      }

      printWarning.apply(undefined, [format].concat(args));
    }
  };
}

var lowPriorityWarning$1 = lowPriorityWarning;

function typeOf(object) {
  if (typeof object === 'object' && object !== null) {
    var $$typeof = object.$$typeof;
    switch ($$typeof) {
      case REACT_ELEMENT_TYPE:
        var type = object.type;

        switch (type) {
          case REACT_ASYNC_MODE_TYPE:
          case REACT_CONCURRENT_MODE_TYPE:
          case REACT_FRAGMENT_TYPE:
          case REACT_PROFILER_TYPE:
          case REACT_STRICT_MODE_TYPE:
          case REACT_SUSPENSE_TYPE:
            return type;
          default:
            var $$typeofType = type && type.$$typeof;

            switch ($$typeofType) {
              case REACT_CONTEXT_TYPE:
              case REACT_FORWARD_REF_TYPE:
              case REACT_PROVIDER_TYPE:
                return $$typeofType;
              default:
                return $$typeof;
            }
        }
      case REACT_LAZY_TYPE:
      case REACT_MEMO_TYPE:
      case REACT_PORTAL_TYPE:
        return $$typeof;
    }
  }

  return undefined;
}

// AsyncMode is deprecated along with isAsyncMode
var AsyncMode = REACT_ASYNC_MODE_TYPE;
var ConcurrentMode = REACT_CONCURRENT_MODE_TYPE;
var ContextConsumer = REACT_CONTEXT_TYPE;
var ContextProvider = REACT_PROVIDER_TYPE;
var Element = REACT_ELEMENT_TYPE;
var ForwardRef = REACT_FORWARD_REF_TYPE;
var Fragment = REACT_FRAGMENT_TYPE;
var Lazy = REACT_LAZY_TYPE;
var Memo = REACT_MEMO_TYPE;
var Portal = REACT_PORTAL_TYPE;
var Profiler = REACT_PROFILER_TYPE;
var StrictMode = REACT_STRICT_MODE_TYPE;
var Suspense = REACT_SUSPENSE_TYPE;

var hasWarnedAboutDeprecatedIsAsyncMode = false;

// AsyncMode should be deprecated
function isAsyncMode(object) {
  {
    if (!hasWarnedAboutDeprecatedIsAsyncMode) {
      hasWarnedAboutDeprecatedIsAsyncMode = true;
      lowPriorityWarning$1(false, 'The ReactIs.isAsyncMode() alias has been deprecated, ' + 'and will be removed in React 17+. Update your code to use ' + 'ReactIs.isConcurrentMode() instead. It has the exact same API.');
    }
  }
  return isConcurrentMode(object) || typeOf(object) === REACT_ASYNC_MODE_TYPE;
}
function isConcurrentMode(object) {
  return typeOf(object) === REACT_CONCURRENT_MODE_TYPE;
}
function isContextConsumer(object) {
  return typeOf(object) === REACT_CONTEXT_TYPE;
}
function isContextProvider(object) {
  return typeOf(object) === REACT_PROVIDER_TYPE;
}
function isElement(object) {
  return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
}
function isForwardRef(object) {
  return typeOf(object) === REACT_FORWARD_REF_TYPE;
}
function isFragment(object) {
  return typeOf(object) === REACT_FRAGMENT_TYPE;
}
function isLazy(object) {
  return typeOf(object) === REACT_LAZY_TYPE;
}
function isMemo(object) {
  return typeOf(object) === REACT_MEMO_TYPE;
}
function isPortal(object) {
  return typeOf(object) === REACT_PORTAL_TYPE;
}
function isProfiler(object) {
  return typeOf(object) === REACT_PROFILER_TYPE;
}
function isStrictMode(object) {
  return typeOf(object) === REACT_STRICT_MODE_TYPE;
}
function isSuspense(object) {
  return typeOf(object) === REACT_SUSPENSE_TYPE;
}

exports.typeOf = typeOf;
exports.AsyncMode = AsyncMode;
exports.ConcurrentMode = ConcurrentMode;
exports.ContextConsumer = ContextConsumer;
exports.ContextProvider = ContextProvider;
exports.Element = Element;
exports.ForwardRef = ForwardRef;
exports.Fragment = Fragment;
exports.Lazy = Lazy;
exports.Memo = Memo;
exports.Portal = Portal;
exports.Profiler = Profiler;
exports.StrictMode = StrictMode;
exports.Suspense = Suspense;
exports.isValidElementType = isValidElementType;
exports.isAsyncMode = isAsyncMode;
exports.isConcurrentMode = isConcurrentMode;
exports.isContextConsumer = isContextConsumer;
exports.isContextProvider = isContextProvider;
exports.isElement = isElement;
exports.isForwardRef = isForwardRef;
exports.isFragment = isFragment;
exports.isLazy = isLazy;
exports.isMemo = isMemo;
exports.isPortal = isPortal;
exports.isProfiler = isProfiler;
exports.isStrictMode = isStrictMode;
exports.isSuspense = isSuspense;
  })();
}
});

unwrapExports(reactIs_development);
var reactIs_development_1 = reactIs_development.typeOf;
var reactIs_development_2 = reactIs_development.AsyncMode;
var reactIs_development_3 = reactIs_development.ConcurrentMode;
var reactIs_development_4 = reactIs_development.ContextConsumer;
var reactIs_development_5 = reactIs_development.ContextProvider;
var reactIs_development_6 = reactIs_development.Element;
var reactIs_development_7 = reactIs_development.ForwardRef;
var reactIs_development_8 = reactIs_development.Fragment;
var reactIs_development_9 = reactIs_development.Lazy;
var reactIs_development_10 = reactIs_development.Memo;
var reactIs_development_11 = reactIs_development.Portal;
var reactIs_development_12 = reactIs_development.Profiler;
var reactIs_development_13 = reactIs_development.StrictMode;
var reactIs_development_14 = reactIs_development.Suspense;
var reactIs_development_15 = reactIs_development.isValidElementType;
var reactIs_development_16 = reactIs_development.isAsyncMode;
var reactIs_development_17 = reactIs_development.isConcurrentMode;
var reactIs_development_18 = reactIs_development.isContextConsumer;
var reactIs_development_19 = reactIs_development.isContextProvider;
var reactIs_development_20 = reactIs_development.isElement;
var reactIs_development_21 = reactIs_development.isForwardRef;
var reactIs_development_22 = reactIs_development.isFragment;
var reactIs_development_23 = reactIs_development.isLazy;
var reactIs_development_24 = reactIs_development.isMemo;
var reactIs_development_25 = reactIs_development.isPortal;
var reactIs_development_26 = reactIs_development.isProfiler;
var reactIs_development_27 = reactIs_development.isStrictMode;
var reactIs_development_28 = reactIs_development.isSuspense;

var reactIs = createCommonjsModule(function (module) {

if (process.env.NODE_ENV === 'production') {
  module.exports = reactIs_production_min;
} else {
  module.exports = reactIs_development;
}
});

/**
 * Copyright 2015, Yahoo! Inc.
 * Copyrights licensed under the New BSD License. See the accompanying LICENSE file for terms.
 */

var REACT_STATICS = {
    childContextTypes: true,
    contextType: true,
    contextTypes: true,
    defaultProps: true,
    displayName: true,
    getDefaultProps: true,
    getDerivedStateFromError: true,
    getDerivedStateFromProps: true,
    mixins: true,
    propTypes: true,
    type: true
};

var KNOWN_STATICS = {
    name: true,
    length: true,
    prototype: true,
    caller: true,
    callee: true,
    arguments: true,
    arity: true
};

var FORWARD_REF_STATICS = {
    '$$typeof': true,
    render: true,
    defaultProps: true,
    displayName: true,
    propTypes: true
};

var MEMO_STATICS = {
    '$$typeof': true,
    compare: true,
    defaultProps: true,
    displayName: true,
    propTypes: true,
    type: true
};

var TYPE_STATICS = {};
TYPE_STATICS[reactIs.ForwardRef] = FORWARD_REF_STATICS;

function getStatics(component) {
    if (reactIs.isMemo(component)) {
        return MEMO_STATICS;
    }
    return TYPE_STATICS[component['$$typeof']] || REACT_STATICS;
}

var defineProperty = Object.defineProperty;
var getOwnPropertyNames = Object.getOwnPropertyNames;
var getOwnPropertySymbols = Object.getOwnPropertySymbols;
var getOwnPropertyDescriptor = Object.getOwnPropertyDescriptor;
var getPrototypeOf = Object.getPrototypeOf;
var objectPrototype = Object.prototype;

function hoistNonReactStatics(targetComponent, sourceComponent, blacklist) {
    if (typeof sourceComponent !== 'string') {
        // don't hoist over string (html) components

        if (objectPrototype) {
            var inheritedComponent = getPrototypeOf(sourceComponent);
            if (inheritedComponent && inheritedComponent !== objectPrototype) {
                hoistNonReactStatics(targetComponent, inheritedComponent, blacklist);
            }
        }

        var keys = getOwnPropertyNames(sourceComponent);

        if (getOwnPropertySymbols) {
            keys = keys.concat(getOwnPropertySymbols(sourceComponent));
        }

        var targetStatics = getStatics(targetComponent);
        var sourceStatics = getStatics(sourceComponent);

        for (var i = 0; i < keys.length; ++i) {
            var key = keys[i];
            if (!KNOWN_STATICS[key] && !(blacklist && blacklist[key]) && !(sourceStatics && sourceStatics[key]) && !(targetStatics && targetStatics[key])) {
                var descriptor = getOwnPropertyDescriptor(sourceComponent, key);
                try {
                    // Avoid failures from read-only properties
                    defineProperty(targetComponent, key, descriptor);
                } catch (e) {}
            }
        }

        return targetComponent;
    }

    return targetComponent;
}

var hoistNonReactStatics_cjs = hoistNonReactStatics;

var LRSCtx = createContext(undefined);

function useLRS() {
    return useContext(LRSCtx);
}

var LinkRenderCtx = createContext({
    subject: undefined,
    topology: DEFAULT_TOPOLOGY,
});
var useLinkRenderContext = function () { return useContext(LinkRenderCtx); };
var Consumer = LinkRenderCtx.Consumer, Provider = LinkRenderCtx.Provider;
function useCalculateChildProps(props, context, options) {
    if (options === void 0) { options = {}; }
    var lrs = useLRS();
    var subject = context.subject, topology = context.topology;
    var overrides = {};
    if (typeof props.innerRef !== "undefined") {
        overrides.ref = props.innerRef;
        overrides.innerRef = undefined;
    }
    if (options.subject) {
        overrides.subjectCtx = subject;
        overrides.subject = props.subject;
    }
    if (options.topology) {
        overrides.topologyCtx = topology;
        overrides.topology = props.topology === null ? DEFAULT_TOPOLOGY : props.topology;
    }
    if (options.lrs) {
        overrides.lrs = lrs;
    }
    if (options.helpers) {
        overrides.reset = options.helpers.reset;
        overrides.reloadLinkedObject = function () {
            return lrs.getEntity((props.subject || subject), { reload: true });
        };
    }
    return Object.assign({}, props, { subject: subject, topology: topology }, overrides);
}
function withLinkCtx(Component, options) {
    if (options === void 0) { options = { lrs: true }; }
    var Comp = function (props) {
        var context = useLinkRenderContext();
        var childProps = useCalculateChildProps(props, context, options);
        return createElement(Component, childProps);
    };
    Comp.displayName = "withLinkCtxWrapper";
    return hoistNonReactStatics_cjs(Comp, Component);
}

/**
 * The subjects this component subscribes to.
 * Includes the subject by default.
 */
function normalizeDataSubjects(props) {
    if (!(props.subject || props.dataSubjects)) {
        return [];
    }
    var result;
    if (props.dataSubjects) {
        result = Array.isArray(props.dataSubjects)
            ? __spreadArrays([props.subject], props.dataSubjects) : [props.subject, props.dataSubjects];
    }
    else {
        result = [props.subject];
    }
    if (props.subject && props.subject.termType === TermType.NamedNode) {
        var doc = rdfFactory.namedNode(rdflib.URI.docpart(props.subject.value));
        if (!rdfFactory.equals(doc, props.subject)) {
            result.push(doc);
        }
    }
    return result;
}
/**
 * Re-renders when {props.subject} or a resource mentioned in {props.dataSubjects} changes in the store.
 *
 * Be sure to keep undefined values in subject or dataSubjects to ensure useEffect DependencyList consistency.
 */
function useDataInvalidation(props) {
    var lrs = useLRS();
    var _a = useState(lrs.store.changeTimestamps[rdfFactory.id(props.subject)]), lastUpdate = _a[0], setInvalidate = _a[1];
    function handleStatusChange(_, lastUpdateAt) {
        setInvalidate(lastUpdateAt);
    }
    useEffect(function () {
        var subscriptionSubjects = normalizeDataSubjects(props);
        return lrs.subscribe({
            callback: handleStatusChange,
            lastUpdateAt: undefined,
            markedForDelete: false,
            onlySubjects: true,
            subjectFilter: subscriptionSubjects.filter(Boolean),
        });
    }, [
        rdfFactory.id(props.subject),
        normalizeType(props.dataSubjects)
            .filter(Boolean)
            .map(function (n) { return rdfFactory.id(n); })
            .reduce(function (a, b) { return a + b; }, 0),
    ]);
    return lastUpdate;
}

var blankNodeWarn = "Cannot load a blank node since it has no defined way to be resolved.";
/**
 * Fetches the {props.subject} if it has no data or is expired.
 *
 * @param lastUpdate The resource update time from {useDataInvalidation}, .
 * @param setError Is called when trying to load blank nodes, otherwise the request will fail.
 *
 * @see {LinkedRenderStore#shouldLoadResource} for the triggering mechanism.
 */
function useDataFetching(props, lastUpdate, setError) {
    var lrs = useLRS();
    useEffect(function () {
        if (props.subject && lrs.shouldLoadResource(props.subject)) {
            if (setError && props.subject.termType === TermType.BlankNode) {
                return setError(new TypeError(blankNodeWarn));
            }
            if (!!props.fetch || true) {
                lrs.queueEntity(props.subject);
            }
        }
    }, [rdfFactory.id(props.subject), lastUpdate]);
}

function nextRenderContext(props) {
    return {
        subject: props.subject,
        topology: props.topology || props.topologyCtx,
    };
}
function wrapRenderContext(props, comp) {
    return createElement(Provider, { value: nextRenderContext(props) }, comp);
}
function renderNoView(props, lrs) {
    var NoView = lrs.getComponentForProperty(defaultNS.ll("NoView"), (props.label || RENDER_CLASS_NAME), props.topology || props.topologyCtx);
    if (NoView) {
        return createElement(NoView);
    }
    // tslint:disable-next-line no-console
    console.log("no-view", props.subject, lrs.getStatus(props.subject));
    return createElement("div", { className: "no-view" }, createElement("p", null, "We currently don't have a view for this (" + props.subject + ")"));
}
function renderError(props, lrs, error) {
    var errComp = errorComponent(props, lrs);
    if (errComp) {
        return wrapRenderContext(props, createElement(errComp, Object.assign({}, props, {
            error: error instanceof Error ? error : undefined,
            linkRequestStatus: lrs.getStatus(props.subject || props.subjectCtx),
            report: lrs.report,
            subject: props.subject,
        })));
    }
    return null;
}
function errorComponent(props, lrs) {
    return props.onError
        || lrs.getComponentForType(defaultNS.ll("ErrorResource"), props.topology || props.topologyCtx)
        || null;
}
function loadingComponent(props, lrs) {
    return props.onLoad
        || lrs.getComponentForType(defaultNS.ll("LoadingResource"), props.topology || props.topologyCtx)
        || null;
}

function useRenderLoadingOrError(props, error) {
    var lrs = useLRS();
    if (error) {
        return renderError(props, lrs, error);
    }
    var status = lrs.getStatus(props.subject);
    if (status.status === ACCEPTED || lrs.shouldLoadResource(props.subject)) {
        var loadComp = loadingComponent(props, lrs);
        return loadComp === null
            ? null
            : wrapRenderContext(props, React__default.createElement(loadComp, props));
    }
    if (status.status >= BAD_REQUEST) {
        return renderError(props, lrs, error);
    }
    return undefined;
}

function useCalculatedViewWithState(props, lrs, error) {
    if (props.forceRender && props.children) {
        return createElement(Fragment, null, props.children);
    }
    var notReadyComponent = useRenderLoadingOrError(props, error);
    if (notReadyComponent !== undefined) {
        return notReadyComponent;
    }
    if (props.children) {
        return createElement(Fragment, null, props.children);
    }
    var component = lrs.resourceComponent(props.subject, props.topology || props.topologyCtx);
    if (component !== undefined) {
        return createElement(component, props);
    }
    return renderNoView(props, lrs);
}
function LRC(props, _) {
    var context = useLinkRenderContext();
    var _a = useState(undefined), error = _a[0], setError = _a[1];
    var options = {
        helpers: {
            reset: function () { return setError(undefined); },
        },
        subject: true,
        topology: true,
    };
    var lrs = useLRS();
    var childProps = useCalculateChildProps(props, context, options);
    var lastUpdate = useDataInvalidation(childProps);
    useDataFetching(childProps, lastUpdate, setError);
    var comp = useCalculatedViewWithState(childProps, lrs, error);
    return wrapRenderContext(childProps, comp);
}
LRC.defaultProps = {
    children: undefined,
    forceRender: false,
    onError: undefined,
    onLoad: undefined,
    topology: undefined,
};
LRC.displayName = "LinkedResourceContainer";
var LinkedResourceContainer = forwardRef(function (props, ref) { return createElement(LRC, __assign({ innerRef: ref }, props)); });

var nodeTypes = [TermType.NamedNode, TermType.BlankNode];
function getLinkedObjectClass(_a, lrs, labelOverride) {
    var label = _a.label, subject = _a.subject, topology = _a.topology, topologyCtx = _a.topologyCtx;
    return lrs.resourcePropertyComponent(subject, labelOverride || label, topology === null ? undefined : topology || topologyCtx);
}
function limitTimes(props, objRaw, lrs, func, associationRenderer) {
    var associationProps = associationRenderer !== Fragment ? props : null;
    if (objRaw.length === 0) {
        return null;
    }
    else if (objRaw.length === 1) {
        return createElement(associationRenderer, associationProps, func(objRaw[0]));
    }
    else if (props.limit === 1) {
        return createElement(associationRenderer, associationProps, 
        // @ts-ignore
        func(getTermBestLang(objRaw, lrs.store.langPrefs)));
    }
    var pLimit = Math.min.apply(Math, [props.limit, objRaw.length].filter(Number));
    var elems = new Array(pLimit);
    for (var i = 0; i < pLimit; i++) {
        elems.push(func(objRaw[i]));
    }
    return createElement(associationRenderer, associationProps, elems);
}
function renderChildrenOrValue(props, lrs) {
    return function (p) {
        if (props.children || p.termType !== TermType.Literal) {
            return createElement(Fragment, null, props.children || p.value);
        }
        var topology = props.topology, topologyCtx = props.topologyCtx, subjectCtx = props.subjectCtx;
        var literalRenderer = lrs.getComponentForProperty(rdfs.Literal, rdfFactory.namedNode(p.datatype.value), topology === null ? undefined : topology || topologyCtx);
        if (!literalRenderer) {
            return createElement(Fragment, null, p.value);
        }
        return createElement(literalRenderer, {
            linkedProp: p,
            subject: p.datatype,
            subjectCtx: subjectCtx,
            topology: topology,
            topologyCtx: topologyCtx,
        });
    };
}
function Prop(props) {
    var options = { topology: true };
    var lrs = useLRS();
    var _a = useState(undefined), error = _a[0], setError = _a[1];
    var context = useLinkRenderContext();
    var subjectData = lrs.tryEntity(context.subject);
    var childProps = useCalculateChildProps(props, context, options);
    try {
        useDataInvalidation(childProps);
    }
    catch (e) {
        setError(e);
    }
    if (subjectData.length === 0) {
        return null;
    }
    var labels = normalizeType(childProps.label).map(function (l) { return l.value; });
    var objRaw = subjectData
        .filter(function (s) { return labels.includes(s.predicate.value); })
        .map(function (s) { return s.object; });
    if (error) {
        return renderError(childProps, lrs, error);
    }
    if (objRaw.length === 0 && !childProps.forceRender) {
        return null;
    }
    var associationRenderer = getLinkedObjectClass(childProps, lrs, rdf.predicate) || Fragment;
    var associationProps = associationRenderer !== Fragment ? childProps : null;
    var childComp = typeof childProps.children === "function" ? childProps.children(objRaw) : childProps.children;
    if (typeof childProps.children === "function") {
        return createElement(associationRenderer, associationProps, childComp);
    }
    var component = getLinkedObjectClass(childProps, lrs);
    if (component) {
        var toRender = limitTimes(childProps, objRaw, lrs, function (p) { return createElement(component, __assign(__assign({}, childProps), { linkedProp: p }), childComp); }, associationRenderer);
        if (toRender === null) {
            return createElement(associationRenderer, associationProps, createElement(component, __assign({}, childProps), childComp));
        }
        return toRender;
    }
    else if (objRaw.length > 0) {
        if (nodeTypes.includes(objRaw[0].termType)) {
            var wrapLOC = function (p) {
                var lrcProps = __assign(__assign({}, childProps), { subject: p });
                // return React.createElement(LRC, lrcProps, childComp);
                return createElement(LinkedResourceContainer, __assign({}, lrcProps), childComp);
            };
            return limitTimes(childProps, objRaw, lrs, wrapLOC, associationRenderer);
        }
        return limitTimes(childProps, objRaw, lrs, renderChildrenOrValue(childProps, lrs), associationRenderer);
    }
    if (childProps.children) {
        return createElement(associationRenderer, associationProps, childComp);
    }
    return null;
}
Prop.defaultProps = {
    forceRender: false,
    limit: 1,
    linkedProp: undefined,
};
Prop.displayName = "Property";
var Property$1 = memo(Prop);

/** Function to create arbitrary terms within the 'XML Schema Datatypes in RDF and OWL' ontology */
const ns$2 = createNS("http://www.w3.org/2001/XMLSchema#");
const string = ns$2("string");
const xsdboolean = ns$2("boolean");
const decimal = ns$2("decimal");
const xsdfloat = ns$2("float");
const xsddouble = ns$2("double");
const duration = ns$2("duration");
const dateTime = ns$2("dateTime");
const time = ns$2("time");
const date = ns$2("date");
const gYearMonth = ns$2("gYearMonth");
const gYear = ns$2("gYear");
const gMonthDay = ns$2("gMonthDay");
const gDay = ns$2("gDay");
const gMonth = ns$2("gMonth");
const hexBinary = ns$2("hexBinary");
const base64Binary = ns$2("base64Binary");
const anyURI = ns$2("anyURI");
const normalizedString = ns$2("normalizedString");
const token = ns$2("token");
const language = ns$2("language");
const NMTOKEN = ns$2("NMTOKEN");
const Name = ns$2("Name");
const NCName = ns$2("NCName");
const integer = ns$2("integer");
const nonPositiveInteger = ns$2("nonPositiveInteger");
const negativeInteger = ns$2("negativeInteger");
const xsdlong = ns$2("long");
const xsdint = ns$2("int");
const xsdshort = ns$2("short");
const xsdbyte = ns$2("byte");
const nonNegativeInteger = ns$2("nonNegativeInteger");
const unsignedLong = ns$2("unsignedLong");
const unsignedInt = ns$2("unsignedInt");
const unsignedShort = ns$2("unsignedShort");
const unsignedByte = ns$2("unsignedByte");
const positiveInteger = ns$2("positiveInteger");
const yearMonthDuration = ns$2("yearMonthDuration");
const dayTimeDuration = ns$2("dayTimeDuration");
const dateTimeStamp = ns$2("dateTimeStamp");
var xsd = {
    /** Function to create arbitrary terms within the 'XML Schema Datatypes in RDF and OWL' ontology */
    ns: ns$2,
    string,
    boolean: xsdboolean,
    decimal,
    float: xsdfloat,
    double: xsddouble,
    duration,
    dateTime,
    time,
    date,
    gYearMonth,
    gYear,
    gMonthDay,
    gDay,
    gMonth,
    hexBinary,
    base64Binary,
    anyURI,
    normalizedString,
    token,
    language,
    NMTOKEN,
    Name,
    NCName,
    integer,
    nonPositiveInteger,
    negativeInteger,
    long: xsdlong,
    int: xsdint,
    short: xsdshort,
    byte: xsdbyte,
    nonNegativeInteger,
    unsignedLong,
    unsignedInt,
    unsignedShort,
    unsignedByte,
    positiveInteger,
    yearMonthDuration,
    dayTimeDuration,
    dateTimeStamp
};

var globalLinkOptsDefaults = {
    forceRender: false,
    label: undefined,
    limit: 1,
    name: undefined,
    returnType: "term",
};
function term(iri) {
    return iri.term || iri.value.split(/[\/#]/).pop().split("?").shift() || "";
}
var numberTypes = [
    xsd.integer,
    xsd.int,
    xsd.unsignedInt,
    xsd.short,
    xsd.unsignedShort,
    xsd.byte,
    xsd.unsignedByte,
    xsd.float,
    xsd.decimal,
];
// From rdflib.js with modifications
function toJS(obj) {
    if (!isLiteral(obj)) {
        return obj;
    }
    if (rdfFactory.equals(obj.datatype, xsd.boolean)) {
        return obj.value === "true" || obj.value === "1" || obj.value === "t";
    }
    if (rdfFactory.equals(obj.datatype, xsd.dateTime) ||
        rdfFactory.equals(obj.datatype, xsd.date)) {
        return new Date(obj.value);
    }
    if (numberTypes.some(function (type) { return rdfFactory.equals(obj.datatype, type); })) {
        return Number(obj.value);
    }
    return obj.value;
}
function toReturnType(returnType, p) {
    switch (returnType) {
        case "literal":
            return toJS(p.object);
        case "value":
            return p.object.value;
        case "term":
            return p.object;
        case "statement":
            return p;
    }
}
function dataPropsToPropMap(mapDataToProps, opts) {
    var propMap = {};
    var requestedProperties = Array.isArray(mapDataToProps) ? mapDataToProps.map(function (p) { return rdfFactory.id(p); }) : [];
    if (Array.isArray(mapDataToProps)) {
        if (mapDataToProps.length === 0) {
            throw new TypeError("Props array must contain at least one predicate");
        }
        mapDataToProps.forEach(function (prop) {
            propMap[term(prop)] = {
                forceRender: opts.forceRender || globalLinkOptsDefaults.forceRender,
                label: [prop],
                limit: opts.limit || globalLinkOptsDefaults.limit,
                name: term(prop),
            };
        });
    }
    else {
        for (var propKey in mapDataToProps) {
            if (!mapDataToProps.hasOwnProperty(propKey)) {
                continue;
            }
            var predObj = mapDataToProps[propKey];
            if (Array.isArray(predObj)) {
                if (predObj.length === 0) {
                    throw new TypeError("Props array must contain at least one predicate");
                }
                requestedProperties.push.apply(requestedProperties, predObj.map(function (p) { return rdfFactory.id(p); }));
                propMap[propKey || term(predObj[0])] = {
                    forceRender: opts.forceRender || globalLinkOptsDefaults.forceRender,
                    label: predObj,
                    limit: opts.limit || globalLinkOptsDefaults.limit,
                    name: propKey || term(predObj[0]),
                    returnType: opts.returnType || globalLinkOptsDefaults.returnType,
                };
            }
            else if (isNamedNode(predObj)) {
                requestedProperties.push(rdfFactory.id(predObj));
                propMap[propKey || term(predObj)] = {
                    forceRender: opts.forceRender || globalLinkOptsDefaults.forceRender,
                    label: [predObj],
                    limit: opts.limit || globalLinkOptsDefaults.limit,
                    name: propKey || term(predObj),
                };
            }
            else {
                if (predObj.label === undefined) {
                    throw new TypeError("Inner opts label must be a single NamedNode");
                }
                var labelArr = Array.isArray(predObj.label) ? predObj.label : [predObj.label];
                labelArr.forEach(function (label) {
                    requestedProperties.push(rdfFactory.id(label));
                });
                propMap[propKey || predObj.name || term(labelArr[0])] = {
                    forceRender: predObj.forceRender || opts.forceRender || globalLinkOptsDefaults.forceRender,
                    label: normalizeType(predObj.label),
                    limit: predObj.limit || opts.limit || globalLinkOptsDefaults.limit,
                    linkedProp: predObj.linkedProp || opts.linkedProp || globalLinkOptsDefaults.linkedProp,
                    name: propKey || predObj.name || term(labelArr[0]),
                    returnType: predObj.returnType || opts.returnType || globalLinkOptsDefaults.returnType,
                };
            }
        }
    }
    return [propMap, requestedProperties];
}
/**
 * Binds a react component to data properties.
 *
 * The current implementation only supports a one-dimensional array of NamedNode objects. And binds
 * the underlying values to the `props` object with each predicate term as the key.
 *
 * @example
 * ```
 *
 *   const BlogPost = (props) => (
 *     <div>
 *         <h1>{props.name.value}</h1>
 *         <p>{props.text.value}</p>
 *         <LinkedResourceContainer subject={props.author} />
 *     </div
 *   )
 *
 *   link([NS.schema("name"), NS.schema("text"), NS.schema("author")])(BlogPost)
 * ```
 * @param {NamedNode[]} mapDataToProps The properties to bind to the component, only NamedNode[] is currently supported.
 * @param {LinkOpts} opts Adjust the default behaviour, these are not yet guaranteed.
 */
function link(mapDataToProps, opts) {
    if (opts === void 0) { opts = globalLinkOptsDefaults; }
    var _a = dataPropsToPropMap(mapDataToProps, opts), propMap = _a[0], requestedProperties = _a[1];
    if (requestedProperties.length === 0) {
        throw new TypeError("Props array must contain at least one predicate");
    }
    var returnType = opts.returnType || "term";
    function getLinkedObjectProperties(lrs, context, subjProps) {
        var acc = {};
        for (var _i = 0, _a = Object.values(propMap); _i < _a.length; _i++) {
            var propOpts = _a[_i];
            var _loop_1 = function (cur) {
                if (acc[propOpts.name]) {
                    return "continue";
                }
                if (propOpts.limit === 1) {
                    var p = getPropBestLangRaw(lrs.getResourcePropertyRaw(context.subject, cur), lrs.store.langPrefs);
                    if (p) {
                        acc[propOpts.name] = toReturnType(returnType, p);
                    }
                }
                else {
                    acc[propOpts.name] = subjProps
                        .filter(function (s) { return rdfFactory.id(s.predicate) === rdfFactory.id(cur); })
                        .map(function (s) { return toReturnType(returnType, s); });
                }
            };
            for (var _b = 0, _c = propOpts.label; _b < _c.length; _b++) {
                var cur = _c[_b];
                _loop_1(cur);
            }
        }
        return acc;
    }
    return function wrapWithConnect(wrappedComponent) {
        var comp = forwardRef(function (props, ref) {
            var lrs = useLRS();
            var context = useLinkRenderContext();
            var childProps = useCalculateChildProps(__assign(__assign({}, props), { innerRef: ref }), context, { lrs: true });
            var subjectData = lrs.tryEntity(childProps.subject);
            var subjProps = subjectData
                .filter(function (s) { return requestedProperties.includes(rdfFactory.id(s.predicate)); });
            var mappedProps = __assign(__assign({}, childProps), getLinkedObjectProperties(lrs, context, subjProps));
            var linkVersion = useDataInvalidation(mappedProps);
            if ((props.forceRender || opts.forceRender) !== true && subjProps.length === 0) {
                return null;
            }
            return createElement(wrappedComponent, __assign(__assign({}, mappedProps), { linkVersion: linkVersion }));
        });
        return hoistNonReactStatics_cjs(comp, wrappedComponent);
    };
}

var BlankNode = shape({
    termType: oneOf(["BlankNode"]),
    value: string$1,
});
var NamedNode = shape({
    termType: oneOf(["NamedNode"]),
    value: string$1,
});
var Literal$1 = shape({
    datatype: NamedNode,
    language: string$1,
    termType: oneOf(["Literal"]),
    value: string$1,
});
var Statement$1 = shape({
    subject: oneOfType([BlankNode, NamedNode]),
    predicate: oneOfType([BlankNode, NamedNode]),
    object: oneOfType([BlankNode, NamedNode, Literal$1]),
    graph: oneOfType([BlankNode, NamedNode]),
});
var namedOrBlankNode = oneOfType([
    NamedNode,
    BlankNode,
]);
var linkType = oneOfType([
    NamedNode,
    BlankNode,
    Literal$1,
    Statement$1,
    string$1,
    arrayOf(NamedNode),
    arrayOf(BlankNode),
    arrayOf(Literal$1),
    arrayOf(Statement$1),
    arrayOf(string$1),
]);
var labelType = oneOfType([
    NamedNode,
    arrayOf(NamedNode),
]);
var linkedPropType = oneOfType([
    NamedNode,
    BlankNode,
    Literal$1,
]);
var lrsType = instanceOf(LinkedRenderStore);
var subjectType = namedOrBlankNode;
var topologyType = namedOrBlankNode;

/**
 * @deprecated
 */
var PropertyBase = /** @class */ (function (_super) {
    __extends(PropertyBase, _super);
    function PropertyBase() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    PropertyBase.prototype.componentDidMount = function () {
        this.resubscribe();
    };
    PropertyBase.prototype.componentDidUpdate = function () {
        this.resubscribe();
    };
    PropertyBase.prototype.componentWillUnmount = function () {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    };
    PropertyBase.prototype.render = function () {
        var prop = this.getLinkedObjectProperty();
        return createElement("span", null, "PropBase: " + (prop && prop.value));
    };
    PropertyBase.prototype.shouldComponentUpdate = function (nextProps) {
        if (nextProps.label === undefined) {
            return false;
        }
        return this.props.subject !== nextProps.subject;
    };
    PropertyBase.prototype.getLinkedObjectProperty = function (property) {
        if (property === undefined && typeof this.props.linkedProp !== "undefined") {
            return this.props.linkedProp;
        }
        return this.context.getResourceProperty(this.props.subject, property || this.props.label);
    };
    PropertyBase.prototype.getLinkedObjectPropertyRaw = function (property) {
        return this.context.getResourcePropertyRaw(this.props.subject, property || this.props.label);
    };
    PropertyBase.prototype.resubscribe = function (props) {
        var _this = this;
        if (props === void 0) { props = this.props; }
        var unsubscribe;
        var subs = normalizeDataSubjects(props);
        if (subs.length > 0) {
            unsubscribe = this.context.subscribe({
                callback: function () { return _this.forceUpdate(); },
                markedForDelete: false,
                onlySubjects: true,
                subjectFilter: subs,
            });
        }
        if (this.unsubscribe) {
            this.unsubscribe();
        }
        this.unsubscribe = unsubscribe;
    };
    PropertyBase.contextType = LRSCtx;
    PropertyBase.propTypes = {
        label: labelType,
        linkedProp: linkedPropType,
        subject: subjectType,
    };
    return PropertyBase;
}(Component));

function RenderStoreProvider(_a) {
    var children = _a.children, value = _a.value;
    return createElement(LRSCtx.Provider, { value: value }, children);
}

/**
 * Inherit from this component to set the topology.
 *
 * @example
 * ```
 *
 *   class Collection extends TopologyProvider {
 *     this.topology = NS.app('collection')
 *     // Optionally:
 *     this.className = "Collection";
 *     this.elementName = "span";
 *   }
 * ```
 */
var TopologyProvider = /** @class */ (function (_super) {
    __extends(TopologyProvider, _super);
    function TopologyProvider() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.className = undefined;
        _this.elementType = "div";
        _this.topology = null;
        return _this;
    }
    TopologyProvider.prototype.wrap = function (children) {
        var _this = this;
        return createElement(Consumer, null, function (_a) {
            var subject = _a.subject;
            return createElement(Provider, {
                value: {
                    subject: subject,
                    topology: _this.topology === null ? undefined : _this.topology,
                },
            }, typeof children === "function" ? children(subject) : children);
        });
    };
    TopologyProvider.prototype.render = function () {
        var children = this.props.children || null;
        if (this.className !== undefined) {
            children = createElement(this.elementType, __assign({ className: this.className }, (this.props.elementProps || {})), this.props.children);
        }
        return this.wrap(children);
    };
    TopologyProvider.propTypes = {
        children: node,
        elementProps: object$1,
    };
    return TopologyProvider;
}(Component));

function Type(props, _) {
    var options = {};
    var lrs = useLRS();
    var context = useLinkRenderContext();
    var childProps = useCalculateChildProps(props, context, options);
    useDataInvalidation(childProps);
    var notReadyComponent = useRenderLoadingOrError(childProps);
    if (notReadyComponent !== undefined) {
        return notReadyComponent;
    }
    var component = lrs.resourcePropertyComponent(childProps.subject, (childProps.label || RENDER_CLASS_NAME), childProps.topology || childProps.topologyCtx);
    if (component !== undefined) {
        var _a = childProps, children = _a.children, rest = __rest(_a, ["children"]) // tslint:disable-line trailing-comma
        ;
        return createElement(component, rest, children);
    }
    return renderNoView(childProps, lrs);
}

function withLRS(Component) {
    var Comp = function (props) {
        var lrs = useLRS();
        return createElement(Component, __assign({}, props, { lrs: lrs }));
    };
    return hoistNonReactStatics_cjs(Comp, Component);
}

function withTopology(Component) {
    return function (props) { return (createElement(Consumer, null, function (_a) {
        var topology = _a.topology;
        var merged = __assign(__assign({}, props), { topology: topology });
        return createElement(Component, __assign({}, merged));
    })); };
}

function useResourceView(subject, topology) {
    if (topology === void 0) { topology = DEFAULT_TOPOLOGY; }
    var lrs = useLRS();
    var context = useLinkRenderContext();
    var _a = useState(undefined), view = _a[0], setView = _a[1];
    useEffect(function () {
        setView(lrs.resourceComponent(subject || context.subject, topology || context.topology));
    }, [
        rdfFactory.id(subject),
        rdfFactory.id(topology),
        rdfFactory.id(context.subject),
        rdfFactory.id(context.topology),
    ]);
    return view;
}

/**
 * @unstable
 */
function useView(type, predicate, topology) {
    if (predicate === void 0) { predicate = RENDER_CLASS_NAME; }
    var lrs = useLRS();
    var context = useLinkRenderContext();
    return useMemo(function () {
        return lrs.getComponentForProperty(type, predicate, topology || context.topology);
    }, [type, predicate, topology || context.topology]);
}

function register(comp) {
    var hocs = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        hocs[_i - 1] = arguments[_i];
    }
    var reducer = function (prev, cur) { return cur(prev); };
    // @ts-ignore
    var wrappedComp = (comp.hocs || hocs).reduce(reducer, comp);
    var dataBoundComp = comp.mapDataToProps
        ? link(comp.mapDataToProps, comp.linkOpts)(wrappedComp)
        : wrappedComp;
    return LinkedRenderStore.registerRenderer(dataBoundComp, comp.type, comp.property, comp.topology);
}

/** Prone to change */
var unstable = {
    LRSCtx: LRSCtx,
    LinkRenderCtx: LinkRenderCtx,
    errorComponent: errorComponent,
    getLinkedObjectClass: getLinkedObjectClass,
    loadingComponent: loadingComponent,
    renderError: renderError,
    useCalculateChildProps: useCalculateChildProps,
};

export { LinkedResourceContainer, Property$1 as Property, PropertyBase, RenderStoreProvider, TopologyProvider, Type, labelType, link, linkType, linkedPropType, lrsType, register, subjectType, topologyType, unstable, useDataFetching, useDataInvalidation, useLRS, useLinkRenderContext, useResourceView, useView, withLRS, withLinkCtx, withTopology };
//# sourceMappingURL=link-redux.es6.js.map
